import { GetReportsResponse, ListSubscriptionResponse } from '@urc-ui/store/api/ross/types/api-types';
import { GetSubscriptionsResponse, Subscription, SubscriptionDelivery } from '@urc-ui/store/api/subscriptions/types';
import { Account, FacetOption, Schedule, Subscription as RossSubscription, Report as RossReport } from '@urc-ui/store/api/ross/types/types';
import { PRESET_PERIOD } from '@urc-ui/utils/types';
import { FacetEnum } from '@urc-ui/store/api/report-metadata/types';
import { RECURRING_SCHEDULE_FREQUENCY_MAP_ROSS_TO_SSPA } from '@urc-ui/store/api/ross/constants/mappings';
import { getReportsResponse, Report } from '@urc-ui/store/api/reports/types';
import { isMRCCheckboxSelected } from '@urc-ui/utils/mrc-utils';

export const transformRossListSubscriptionsToExistingListSubscriptions = (data: ListSubscriptionResponse): GetSubscriptionsResponse => {
  return {
    subscriptions: data.subscriptions.map(transformSubscription),
    totalRowCount: data.total,
    nextToken: data.nextToken
  };
};

export const transformRossGetReportsToExistingGetReports = (data: GetReportsResponse): getReportsResponse => {
  return {
    reports: data.reports.map(transformReport),
    totalRowCount: data.total,
    nextToken: data.nextToken
  };
};

export const transformSubscription = (subscription: RossSubscription): Subscription => {
  return {
    id: subscription.id,
    name: subscription.name,
    status: subscription.status,
    programType: subscription.reportCategory,
    reportType: subscription.reportType,
    nextScheduleTime: dateToEpoch(subscription.nextRunAt) || undefined,
    reportPeriod: subscription.reportPeriod.preset || PRESET_PERIOD.CUSTOM,
    reportStartDate: dateToEpoch(subscription.reportPeriod.startDate) || undefined,
    reportEndDate: dateToEpoch(subscription.reportPeriod.endDate) || undefined,
    reportStatus: subscription.latestReport?.status,
    creationTimestamp: dateToEpoch(subscription.latestReport?.completedAt) || undefined,
    s3Url: subscription.latestReport?.downloadLink,
    recipients: subscription.deliverySettings?.email || [],
    subscriptionDelivery: transformSchedule(subscription.schedule),
    format: subscription.format,
    timeUnit: getTimeunit(subscription.facetOptions) || '',
    currencyOfVisualization: getCOV(subscription.facetOptions),
    marketplaceIds: getMarketplaceIds(subscription.accounts),
    latestReportId: subscription.latestReport?.id,
    hasMultiTouchAttribution: getMTA(subscription.facetOptions),
    isMRCCheckboxSelected: isMRCCheckboxSelected(subscription.filters)
  };
};

const getMarketplaceIds = (accounts: Account[]): string[] => {
  return accounts.map(account => {
    return account.marketplaceId;
  });
};

const getCOV = (facets: FacetOption[]): string | undefined => {
  const cov = facets.find(facet => facet.id === FacetEnum.COV);
  return cov?.selection[0] ?? undefined;
};

const getTimeunit = (facets: FacetOption[]): string | undefined => {
  const timeunit = facets.find(facet => facet.id === FacetEnum.TIME_UNIT);
  return timeunit?.selection[0] ?? undefined;
};

const getMTA = (facets: FacetOption[]): boolean => {
  const mta = facets.find(facet => facet.id === FacetEnum.MTA);
  return !!mta?.selection?.length;
};

const transformSchedule = (schedule: Schedule): SubscriptionDelivery => {
  const date = schedule.runDate ? new Date(schedule.runDate) : undefined;
  return {
    frequency: RECURRING_SCHEDULE_FREQUENCY_MAP_ROSS_TO_SSPA[schedule.frequency] ?? schedule.frequency,
    scheduledEndDate: schedule.expiration ? dateToEpoch(schedule.expiration) : undefined,
    deliveryDays: schedule.daysOfWeek?.map(day => {
      return day.toUpperCase();
    }),
    deliveryDate: date ? date.getDate() : undefined,
    deliveryMonth: date ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date).toUpperCase() : undefined,
    deliveryYear: date ? date.getFullYear() : undefined,
    hour: date ? date.getHours() : undefined,
    minute: date ? date.getMinutes() : undefined
  };
};

export const transformReport = (report: RossReport): Report => {
  return {
    id: report.id,
    status: report.status,
    rossS3Url: report.downloadLink,
    creationTimestamp: dateToEpoch(report.scheduledAt),
    reportPeriod: report.reportPeriod.preset || PRESET_PERIOD.CUSTOM,
    reportStartDate: dateToEpoch(report.reportPeriod.startDate),
    reportEndDate: dateToEpoch(report.reportPeriod.endDate)
  };
};

const dateToEpoch = (dateString: string | null | undefined): number | undefined => {
  if (!dateString) {
    return undefined;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date.getTime();
};
